import { Circles, ShapeType } from "../components/process-design/body/sidebar/flow/Constants";
import { RowTypes } from "../components/process-design/cell/CellConstants";
import {
  AllFonts,
  BgColors,
  CellIconNames,
  CellIcons,
  HotkeyScopes,
  TextColors,
  TreeDefaultNodeIds,
} from "../utils/constants";
import { icons, ProcessTypes, ProcessItemTypes } from "../utils/icons";
import { SmartSearchHelper } from "./helpers/SmartSearchHelper";
import { produce } from "immer";

const Ids = {
  Views: "func_3",
  Styles: "func_16",
  Fonts: "func_28",
  TextColors: "func_35",
  BgColors: "func_36",
  CellIcons: "func_37",
  Players: "func_52",
  Processes: "func_53",
  Teams: "func_54",
  Sheets: "func_55",
  Favorites: "func_82",
  CircleType: "func_87",
  Grids: "func_112",
  Businesses: "func_113",
};

function findFunctionAndArgument(expression, cursorPosition) {
  // width(20) does'nt actually return correct answer unless we remove that last )
  if (expression.endsWith(")")) cursorPosition--;
  let arg = 0;
  let bracket = 0;
  let functionName = "";
  let i = cursorPosition;

  for (; i > 0; i--) {
    if (expression[i] === ")") {
      bracket++;
    } else if (expression[i] === "(") {
      if (bracket > 0) bracket--;
      else {
        let x = i - 1;
        let funcName = "";
        while (x >= 0 && expression[x].match(/[a-zA-Z_.0-9 ]/)) {
          funcName = expression[x] + funcName;
          x--;
        }
        if (funcName) {
          functionName = funcName;
          break;
        }
      }
    } else if (expression[i] === "," && bracket === 0) {
      arg++;
    }
  }
  return [functionName, arg, i];
}

function getTeam(team) {
  return !team ? TreeDefaultNodeIds.Private : team;
}

export const smartSearchFormulas = [
  "Font Size",
  "Add Arrow",
  "Add Player",
  "Edit Player",
  "Width",
  "Height",
  "Add View",
  "Rename Table",
  "Filter Table",
];

export default {
  namespaced: true,
  state: () => ({
    data: [],
    smartSearchPrevScope: null,
    funcName: null,
    argNum: null,
    totalArg: null,
    parameters: [],
    parameterIds: [],
    processPlayers: [],
  }),
  mutations: {},
  actions: {
    load({ commit, state, rootState, rootGetters, dispatch }) {
      const isNotGrid = () => rootState.cells.current && rootState.cells.current?.type !== "grid";
      const isGrid = () => rootState.cells.current && rootState.cells.current?.type === "grid";
      const isCycle = () => rootState.cells.current && rootState.cells.current?.type === "cycle";

      const isScopeDesigner = () =>
        !!rootState.cells.current && state.smartSearchPrevScope === HotkeyScopes.ProcessDesigner;
      const isScopeOptions = () => isGrid() && state.smartSearchPrevScope === HotkeyScopes.Sidebar.Options;
      const isScopeProcesses = () => state.smartSearchPrevScope === HotkeyScopes.Sidebar.Processes;
      const isScopeGrids = () => state.smartSearchPrevScope === HotkeyScopes.Sidebar.Grids;
      const isScopeBusinesses = () => state.smartSearchPrevScope === HotkeyScopes.Sidebar.Businesses;
      const isScopeOnMainSpots = () => isScopeGrids || isScopeBusinesses || isScopeProcesses || isScopePlayers;
      const isScopePlayers = () => state.smartSearchPrevScope === HotkeyScopes.Sidebar.Players;
      const isScopeTrash = () => state.smartSearchPrevScope === HotkeyScopes.Trash;
      const isScopeProcessFlow = () => state.smartSearchPrevScope === HotkeyScopes.ProcessFlow;
      const isFlowEditorVisible = () => rootGetters["cells/getColumnVisibility"]("Flow");
      const hasMainRange = () => (isScopeDesigner() || isScopeProcessFlow()) && rootGetters["cells/mainRange"]?.id;
      const isActiveTable = () => rootGetters["table/activeTable"](rootGetters["cells/activeCellId"]) !== null;
      const mergeAllowed = () => hasMainRange() && !isActiveTable();
      const isSubscribed = () => rootState.subscription.action.status.loading == "loaded";
      const isFavorite = () => {
        const designId = rootState.cells.current?.id;
        return rootState.preferences.allPreferences.favoriteProcesses?.includes(designId);
      };
      const trashExists = () => {
        const activeSubNavigation = rootState.preferences.allPreferences?.activeSubNavigation;
        return ["businesses", "grids", "processes", "players", "options"].includes(activeSubNavigation);
      };
      const showVerticalLines = () => {
        return rootState.customFlow.showVerticalLines;
      };
      const editTable = () => {
        return rootState.table.activeTable !== null;
      };

      // Dynamically determine if the "Delete Comment" action should be shown based on active cell's metadata
      const checkCellsComment = () => {
        const hasCellComment = rootState.cells.current?.design.cellsMetaData[rootState.cells.activeCellId]?.comment;
        return hasCellComment; // Only show "Delete Comment" if the active cell has a comment
      };

      const isActiveCellPlayer = () => {
        const rows = rootState.cells.current?.design.rows;
        const currentRow = rootGetters["cells/mainRange"]?.range[1];
        return rows[currentRow].type === RowTypes.Activity;
      };

      state.data = [
        { id: "func_29", title: "Top", icon: icons.AlignTop, show: hasMainRange },
        { id: "func_30", title: "Middle", icon: icons.AlignMiddle, show: hasMainRange },
        { id: "func_31", title: "Bottom", icon: icons.AlignBottom, show: hasMainRange },
        { id: "func_32", title: "Left", icon: icons.AlignLeft, show: hasMainRange },
        { id: "func_33", title: "Center", icon: icons.AlignCenter, show: hasMainRange },
        { id: "func_34", title: "Right", icon: icons.AlignRight, show: hasMainRange },
        {
          id: "func_82",
          title: "Favorites",
          icon: icons.Star,
          children: [],
          show: () => true,
        },
        {
          id: "func_1",
          title: "Toggle Favorite",
          icon: () => (isFavorite() ? icons.StarFill : icons.Star),
          show: () => isScopeDesigner(),
        },
        { id: "func_2", title: "ScreenShot", icon: icons.Screenshot, show: isScopeDesigner },
        { id: "func_3", title: "Views", icon: icons.Home, children: [], show: isScopeDesigner },
        { id: "func_6", title: "Full Screen", icon: icons.Maximize, show: () => true },
        { id: "func_7", title: "Copy", icon: icons.Copy, show: hasMainRange },
        { id: "func_8", title: "Cut", icon: icons.Cut, show: hasMainRange },
        { id: "func_9", title: "Paste", icon: icons.Paste, show: hasMainRange },
        { id: "func_12", title: "Merge", icon: icons.Merge, show: mergeAllowed },
        { id: "func_13", title: "Add Comment", icon: icons.AddComment, show: hasMainRange },
        { id: "func_14", title: "Delete Comment", icon: icons.DeleteComment, show: checkCellsComment },
        { id: "func_16", title: "Styles", icon: icons.CustomStyles, children: [], show: hasMainRange },
        { id: "func_17", title: "Go to", icon: icons.Search, show: () => true },
        { id: "func_19", title: "Fill Right", icon: icons.RightArrow, show: hasMainRange },
        { id: "func_20", title: "Fill Down", icon: icons.DownArrow, show: hasMainRange },
        { id: "func_21", title: "Fill Up", icon: icons.UpArrow, show: hasMainRange },
        { id: "func_22", title: "Fill Left", icon: icons.LeftArrow, show: hasMainRange },
        { id: "func_23", title: "Series", icon: icons.Fill, show: hasMainRange },
        { id: "func_37", title: "Icons", icon: icons.CellIcons, children: [], show: hasMainRange },
        { id: "func_24", title: "Navigation Corner", icon: icons.Direction, show: isScopeDesigner },
        { id: "func_25", title: "Data View", icon: icons.DataView, show: isScopeDesigner },
        { id: "func_26", title: "Bold", icon: icons.Bold, show: hasMainRange },
        { id: "func_27", title: "Clear Style", icon: icons.ClearStyle, show: hasMainRange },
        { id: "func_28", title: "Font Family", icon: icons.FontFace, children: [], show: hasMainRange },
        { id: "func_35", title: "Color", icon: icons.Color, children: [], show: hasMainRange },
        {
          id: "func_36",
          title: "Background Color",
          icon: icons.BackgroundColor,
          children: [],
          show: hasMainRange,
        },
        { id: "func_38", title: "English", icon: icons.English, show: () => true },
        { id: "func_39", title: "Deutsch", icon: icons.Deutsch, show: () => true },
        { id: "func_40", title: "Log out", icon: icons.Logout, show: () => true },
        { id: "func_41", title: "Profile", icon: icons.AccountReactivate, show: () => true },
        { id: "func_42", title: "Business Dialog", icon: icons.BusinessDialog, show: isSubscribed },
        { id: "func_43", title: "Add Row", icon: icons.AddRow, show: hasMainRange },
        { id: "func_44", title: "Delete Row", icon: icons.RemoveRow, show: hasMainRange },
        { id: "func_45", title: "Move Row Up", icon: icons.ChevronUp, show: hasMainRange },
        { id: "func_46", title: "Move Row Down", icon: icons.ChevronDown, show: hasMainRange },
        {
          id: "func_47",
          title: "Add Arrow",
          icon: icons.ArrowRight,
          show: () => isNotGrid() && hasMainRange() && isActiveCellPlayer(),
        },
        {
          id: "func_48",
          title: "Delete Arrow",
          icon: icons.DeleteArrow,
          show: () => isNotGrid() && hasMainRange(),
        },
        { id: "func_83", title: "Add Column", icon: icons.AddColumn, show: () => hasMainRange() && isGrid() },
        { id: "func_49", title: "Delete Column", icon: icons.RemoveColumn, show: hasMainRange },
        { id: "func_50", title: "Move Column Right", icon: icons.ChevronRight, show: hasMainRange },
        { id: "func_51", title: "Move Column Left", icon: icons.ChevronLeft, show: hasMainRange },
        { id: "func_52", title: "Players", icon: icons.player, children: [], show: () => true },
        { id: "func_53", title: "Processes", icon: icons.normal, children: [], show: () => true },
        { id: "func_5", title: "Action Flow", icon: icons.ProcessFlow, show: isNotGrid },
        { id: "func_54", title: "teams", icon: icons.team, children: [], show: () => true },
        { id: "func_55", title: "Sheets", icon: icons.SHEETS, children: [], show: isGrid },
        { id: "func_56", title: "New Sheet", icon: icons.sheet, show: isScopeOptions },
        { id: "func_57", title: "Settings", icon: icons.settings, show: () => true },
        { id: "func_58", title: "Red Zone", icon: icons.RedZone, show: () => true },
        { id: "func_59", title: "Game Zone", icon: icons.GameZone, show: () => true },
        {
          id: "func_60",
          title: "New Process",
          icon: icons.normal,
          show: () => isScopeProcesses() || isScopeBusinesses(),
        },
        {
          id: "func_61",
          title: "New Posting Process",
          icon: icons.posting,
          show: () => isScopeProcesses() || isScopeBusinesses(),
        },
        { id: "func_62", title: "New Grid", icon: icons.grid, show: () => isScopeGrids() || isScopeBusinesses() },
        {
          id: "func_63",
          title: "Open Process/Grid",
          icon: icons.Open,
          show: () => isScopeProcesses() || isScopeGrids() || isScopeBusinesses(),
        },
        { id: "func_64", title: "New Folder", icon: icons.folder, show: isScopeOnMainSpots },
        {
          id: "func_65",
          title: "Rename Process/Grid",
          icon: icons.Edit,
          show: () => isScopeProcesses() || isScopeGrids() || isScopeBusinesses(),
        },
        {
          id: "func_66",
          title: "Delete Design",
          icon: icons.Trash,
          show: () => isScopeOnMainSpots || isScopeOptions() || isScopeTrash(),
        },
        {
          id: "func_67",
          title: "Toggle Expand",
          icon: icons.Maximize,
          show: isScopeOnMainSpots,
        },
        {
          id: "func_68",
          title: "Trash",
          icon: icons.Trash,
          show: () => trashExists(),
        },
        {
          id: "func_79",
          title: "Hide Trash",
          icon: icons.Trash,
          show: () => trashExists(),
        },
        {
          id: "func_69",
          title: "Restore",
          icon: icons.Restore,
          show: isScopeTrash,
        },
        { id: "func_70", title: "New Player", icon: icons.Plus, show: isScopePlayers },
        { id: "func_71", title: "Rename Player", icon: icons.EditPlayer, show: isScopePlayers },
        { id: "func_72", title: "Rename Sheet", icon: icons.RenameSheet, show: isScopeOptions },
        { id: "func_73", title: "Font Size", icon: icons.FontSize, show: hasMainRange },
        { id: "func_74", title: "Width", icon: icons.Width, show: hasMainRange },
        { id: "func_75", title: "Height", icon: icons.Height, show: hasMainRange },
        {
          id: "func_76",
          title: "Add Player",
          icon: icons.AddPlayer,
          show: () => hasMainRange() && isNotGrid(),
        },
        {
          id: "func_77",
          title: "Edit Player",
          icon: icons.EditPlayer,
          show: () => hasMainRange() && isNotGrid(),
        },
        {
          id: "func_78",
          title: "Add View",
          icon: icons.Home,
          show: isScopeDesigner,
        },
        {
          id: "func_80",
          title: "Hide Row",
          icon: icons.HideRow,
          show: hasMainRange,
        },
        {
          id: "func_81",
          title: "Hide Column",
          icon: icons.HideColumn,
          show: hasMainRange,
        },
        {
          id: "func_84",
          title: "Auto Layout",
          icon: icons.Autolayout,
          show: () => hasMainRange() && isNotGrid() && isFlowEditorVisible(),
        },
        {
          id: "func_85",
          title: "Toggle Action Flow lines",
          icon: () => (showVerticalLines() ? icons.HideLines : icons.Lines),
          show: () => hasMainRange() && isNotGrid() && isFlowEditorVisible(),
        },
        {
          id: "func_86",
          title: "Add Action Flow Arrow",
          icon: icons.FlowArrow,
          show: () => hasMainRange() && isNotGrid() && isFlowEditorVisible(),
        },
        {
          id: "func_87",
          title: "Circle Type",
          icon: icons.Circle,
          children: [],
          show: () => hasMainRange() && isNotGrid(),
        },
        {
          id: "func_88",
          title: "Table",
          icon: () => (editTable() ? icons.TableEdit : icons.AddTable),
          children: [],
          show: () => hasMainRange() && isGrid(),
        },
        {
          id: "func_89",
          title: "Toggle Gridlines",
          icon: icons.Grid,
          children: [],
          show: () => hasMainRange() && isGrid(),
        },
        {
          id: "func_90",
          title: "General",
          icon: icons.General,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_91",
          title: "Number",
          icon: icons.Number,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_92",
          title: "Currency",
          icon: icons.Currency,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_93",
          title: "ShortDate",
          icon: icons.ShortDate,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_94",
          title: "LongDate",
          icon: icons.LongDate,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_95",
          title: "Time",
          icon: icons.Time,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_96",
          title: "Fraction",
          icon: icons.Fraction,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_97",
          title: "Text",
          icon: icons.Text,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_98",
          title: "Percentage",
          icon: icons.Percentage,
          children: [],
          show: hasMainRange,
        },
        {
          id: "func_99",
          title: "Name Manger",
          icon: icons.NameManager,
          children: [],
          show: isGrid,
        },
        {
          id: "func_100",
          title: "Name Range",
          icon: icons.NameRange,
          children: [],
          show: isGrid,
        },
        {
          id: "func_101",
          title: "Process From SAP",
          icon: `${icons[ProcessItemTypes.SAP]}`,
          children: [],
          show: isScopeProcesses,
        },
        {
          id: "func_102",
          title: "Rename Table",
          icon: `${icons.TableEdit}`,
          children: [],
          show: isActiveTable,
        },
        {
          id: "func_103",
          title: "Toggle Auto Append",
          icon: `${icons.TableEdit}`,
          children: [],
          show: isActiveTable,
        },
        {
          id: "func_104",
          title: "Toggle Is Column Formula",
          icon: `${icons.TableEdit}`,
          children: [],
          show: isActiveTable,
        },
        {
          id: "func_105",
          title: "Sort A Z",
          icon: `${icons.Ascending}`,
          children: [],
          show: isActiveTable,
        },
        {
          id: "func_106",
          title: "Sort Z A",
          icon: `${icons.Descending}`,
          children: [],
          show: isActiveTable,
        },
        {
          id: "func_107",
          title: "Filter Table",
          icon: `${icons.Filter}`,
          children: [],
          show: isActiveTable,
        },
        {
          id: "func_108",
          title: "Open Filter",
          icon: `${icons.Filter}`,
          children: [],
          show: isActiveTable,
        },
        {
          id: "func_109",
          title: "Clear Filter",
          icon: `${icons.Filter}`,
          children: [],
          show: isActiveTable,
        },
        { id: "func_110", title: "Formula Box", icon: icons.FormulaBox, show: hasMainRange },
        { id: "func_111", title: "Blue Zone", icon: icons.BlueZone, show: () => true },
        { id: "func_112", title: "Grids", icon: icons[ProcessItemTypes.Grid], children: [], show: () => true },
        {
          id: "func_113",
          title: "Business Case",
          icon: icons[ProcessItemTypes.Business],
          children: [],
          show: () => true,
        },
        {
          id: "func_114",
          title: "Fetch Grid Data",
          icon: icons[ProcessItemTypes.Grid],
          children: [],
          show: () => true,
        },
        {
          id: "func_115",
          title: "Duplicate sheet",
          icon: icons.Duplicate,
          children: [],
          show: isGrid,
        },
        { id: "func_116", title: "Unmerge", icon: icons.Unmerge, show: mergeAllowed },
        {
          id: "func_117",
          title: "Toggle Name Range visibility",
          icon: icons.NameRange,
          show: () => hasMainRange() && isGrid(),
        },
        {
          id: "func_118",
          title: "Start Style",
          icon: icons[ProcessItemTypes.Business],
          children: [],
          show: () => true,
        },
        {
          id: "func_119",
          title: "Load Satellite",
          icon: icons[ProcessItemTypes.Business],
          children: [],
          show: () => true,
        },
        {
          id: "func_120",
          title: "Transfer To Grid",
          icon: icons[ProcessItemTypes.Business],
          children: [],
          show: isCycle,
        },
      ];

      // Last id 119
      dispatch("applyColorCodes");
      dispatch("loadViews");
      dispatch("loadStyles");
      dispatch("loadFonts");
      dispatch("loadTextColors");
      dispatch("loadBgColors");
      dispatch("loadCellIcons");
      dispatch("loadPlayers");
      dispatch("loadProcesses");
      dispatch("loadGrids");
      dispatch("loadBusinesses");
      dispatch("loadTeams");
      dispatch("loadSheets");
      dispatch("loadFavorites");
      dispatch("loadCircles");
    },
    applyColorCodes({ commit, state, rootState }) {
      state.data = produce(state.data, (draft) => {
        draft.forEach((item, index) => {
          if (smartSearchFormulas.includes(item.title)) {
            // Apply purple colors for functions
            item.style = {
              bg: "rgba(235, 167, 235, 1)", // Purple background
              border: "rgba(244, 217, 244, 1)", // Purple border
            };
          } else if (item.children) {
            // Apply blue colors for items with children
            item.style = {
              bg: "rgba(142, 186, 255, 1)", // Dark Blue background
              border: "rgba(206, 224, 252, 1)", // Dark Blue border
            };
          } else {
            // Default color for other items
            item.style = {
              bg: "rgba(188, 199, 255, 1)", // Light Blue background
              border: "rgba(223, 228, 252, 1)", // Light Blue border
            };
          }
        });
      });
    },
    loadCircles({ commit, state, rootState }) {
      for (let index = 0; index < Object.keys(ShapeType).length; index++) {
        state.data
          .find((item) => item.id === Ids.CircleType)
          .children.push({
            id: `circle_${index}`,
            title: `${Object.keys(ShapeType)[index]}`,
            icon: Circles[index],
            show: () => true,
          });
      }
    },
    loadViews({ commit, state, rootState, rootGetters, dispatch }) {
      const viewIndex = state.data.findIndex((item) => item.id === Ids.Views);
      state.data[viewIndex].children = [];
      rootState.views.allViews.forEach((item, index) => {
        state.data[viewIndex].children.push({
          id: `view_${item._id}`,
          title: item.title,
          icon: item.icon,
          show: () => true,
        });
      });
    },
    loadStyles({ commit, state, rootState, rootGetters, dispatch }) {
      const stylesIndex = state.data.findIndex((item) => item.id === Ids.Styles);
      state.data[stylesIndex].children = [];
      rootState.preferences.allPreferences.styles?.forEach((item, index) => {
        state.data[stylesIndex].children.push({
          id: `style_${index}`,
          title: item.name,
          style: item,
          show: () => true,
        });
      });
    },
    loadFonts({ commit, state, rootState, rootGetters, dispatch }) {
      const fontIndex = state.data.findIndex((item) => item.id === Ids.Fonts);
      state.data[fontIndex].children = [];
      AllFonts.forEach((font, index) => {
        state.data[fontIndex].children.push({
          id: `font_${index}`,
          title: font,
          icon: "icon-format-font",
          show: () => true,
        });
      });
    },
    loadTextColors({ commit, state, rootState, rootGetters, dispatch }) {
      const textColorIndex = state.data.findIndex((item) => item.id === Ids.TextColors);
      TextColors.forEach((color, index) => {
        state.data[textColorIndex].children.push({
          id: `color_${index}`,
          title: color,
          style: { bgColor: color },
          show: () => true,
        });
      });
    },
    loadBgColors({ commit, state, rootState, rootGetters, dispatch }) {
      const bgColorIndex = state.data.findIndex((item) => item.id === Ids.BgColors);
      BgColors.forEach((color, index) => {
        state.data[bgColorIndex].children.push({
          id: `bgColor_${index}`,
          title: color.title,
          style: { bgColor: color.code },
          show: () => true,
        });
      });
    },
    loadCellIcons({ commit, state, rootState, rootGetters, dispatch }) {
      const cellIconsIndex = state.data.findIndex((item) => item.id === Ids.CellIcons);
      CellIcons.forEach((icon, index) => {
        state.data[cellIconsIndex].children.push({
          id: `icon_${index}`,
          title: CellIconNames[index],
          icon,
          show: () => true,
        });
      });
    },
    loadPlayers({ commit, state, rootState, rootGetters, dispatch }) {
      const playerIndex = state.data.findIndex((item) => item.id === Ids.Players);
      const players = rootState.players.playersHashTable;
      state.data[playerIndex].children = [];
      for (let key in players) {
        if (players[key].parents[1] === TreeDefaultNodeIds.Trash) continue;
        const id = `player_${players[key].id}`;
        const title = players[key].title;
        state.data[playerIndex].children.push({ id, title, icon: "icon-game_character_C", show: () => true });
      }
    },
    loadProcessPlayers({ commit, state, rootState, rootGetters, dispatch }, { team, links }) {
      team = getTeam(team);
      const players = rootState.players.playersHashTable;
      state.processPlayers = [];

      for (let key in players) {
        if (![TreeDefaultNodeIds.Default, team].includes(players[key].parents[1])) continue;
        const id = players[key].id;
        const title = players[key].title;

        state.processPlayers.push({ id, title, icon: "icon-game_character_C", show: () => !(key in links) });
      }
    },
    loadProcesses({ commit, state, rootState, rootGetters, dispatch }) {
      const processIndex = state.data.findIndex((item) => item.id === Ids.Processes);
      const processes = rootState.processes.hashTable;
      state.data[processIndex].children = [];
      for (let key in processes) {
        const type = processes[key].type;
        if (!Object.values(ProcessTypes).includes(type) || processes[key].parents[1] === TreeDefaultNodeIds.Trash)
          continue;
        const id = `process_${processes[key].id}`;
        const title = processes[key].title;
        state.data[processIndex].children.push({ id, title, icon: icons[type], show: () => true });
      }
    },
    loadGrids({ commit, state, rootState, rootGetters, dispatch }) {
      const gridIndex = state.data.findIndex((item) => item.id === Ids.Grids);
      const grids = rootState.grids.hashTable;
      state.data[gridIndex].children = [];
      for (let key in grids) {
        const type = grids[key].type;
        if (!Object.values(ProcessTypes).includes(type) || grids[key].parents[1] === TreeDefaultNodeIds.Trash) continue;
        const id = `grid_${grids[key].id}`;
        const title = grids[key].title;
        state.data[gridIndex].children.push({ id, title, icon: icons[type], show: () => true });
      }
    },
    loadBusinesses({ commit, state, rootState, rootGetters, dispatch }) {
      const businessIndex = state.data.findIndex((item) => item.id === Ids.Businesses);
      const businesses = rootState.businesses.hashTable;
      state.data[businessIndex].children = [];
      for (let key in businesses) {
        const type = businesses[key].type;
        if (!Object.values(ProcessTypes).includes(type) || businesses[key].parents[1] === TreeDefaultNodeIds.Trash)
          continue;
        const id = `business_${businesses[key].id}`;
        const title = businesses[key].title;
        state.data[businessIndex].children.push({ id, title, icon: icons[type], show: () => true });
      }
    },
    loadTeams({ commit, state, rootState, rootGetters, dispatch }) {
      const teamIndex = state.data.findIndex((item) => item.id === Ids.Teams);
      const teams = rootState.teams.teamsHashTable;
      const teamIndexHash = {};

      state.data[teamIndex].children = [];
      for (let key in teams) {
        const type = teams[key].type;
        if (type !== "team_folder") continue;
        const id = `team_${teams[key].id}`;
        const title = teams[key].title;
        state.data[teamIndex].children.push({ id, title, icon: icons[type], children: [], show: () => true });
        teamIndexHash[key] = state.data[teamIndex].children.length - 1;
      }

      for (let key in teams) {
        const type = teams[key].type;
        if (type === "team_folder") continue;
        const id = `member_${teams[key].id}`;
        const title = teams[key].title;

        const lastParent = teams[key].parents[teams[key].parents.length - 1];
        if (!(lastParent in teamIndexHash)) continue;
        state.data[teamIndex].children[teamIndexHash[lastParent]].children.push({
          id,
          title,
          icon: icons[type],
          show: () => true,
        });
      }
    },
    loadSheets({ commit, state, rootState, rootGetters, dispatch }) {
      const sheetIndex = state.data.findIndex((item) => item.id === Ids.Sheets);
      state.data[sheetIndex].children = [];
      const sheets = rootState.sheets.sheetsHashTable;

      rootState.sheets?.allSheets.forEach((sheet) => {
        const parent = sheets[sheet.id].parents[1];
        if (parent === TreeDefaultNodeIds.Trash) return;

        state.data[sheetIndex].children.push({
          id: `sheet_${sheet.sheetId}`,
          title: sheet.name,
          icon: "icon-file-outline",
          show: () => true,
        });
      });
    },
    loadFavorites({ commit, state, rootState, rootGetters, dispatch }) {
      const favoritesIndex = state.data.findIndex((item) => item.id === Ids.Favorites);
      state.data[favoritesIndex].children = [];

      const basePath = rootGetters["preferences/basePath"]; // Get the base path dynamically

      if (!basePath) return;

      const favoritesKey = `favorite${basePath.charAt(0).toUpperCase() + basePath.slice(1)}`;

      const favorites =
        rootState.preferences.allPreferences[favoritesKey]?.map((element) => ({
          id: `${basePath}_${element}`,
          title: rootGetters[`${basePath}/getById`](element)?.title || "",
          icon: icons[rootState[basePath].hashTable[element]?.type],
          show: () => true,
        })) || [];

      state.data[favoritesIndex].children = favorites;
    },
    search({ commit, state, rootState, dispatch }, value) {
      // text delete column should be true when search by dcol
      const isMatch = (item, searchText) => {
        let searchIndex = 0;

        for (let char of String(item).toLowerCase()) {
          if (char === searchText[searchIndex]) {
            searchIndex++;
          }
          if (searchIndex === searchText.length) {
            return true; // All characters found in order
          }
        }

        return false; // Not all characters found
      };
      const queryParts = value
        .toLowerCase()
        .split(">")
        .map((part) => part.trim().toLowerCase());

      function recursiveFilter(arr, parts) {
        const currentPart = parts[0];
        const remainingParts = parts.slice(1);

        const searchTextRegex = /^([\w\s]+)*\(?/;
        const getText = (title) => title.match(searchTextRegex)[1]?.trim() || "";
        arr = arr
          ?.filter((x) => {
            return x.title && isMatch(x.title, getText(currentPart));
          })
          .sort((a, b) => a.title.length - b.title.length);
        if (!arr) return;
        if (remainingParts.length > 0) return recursiveFilter(arr[0].children, remainingParts);
        // show non disabled items first, then disabled items
        const trueArr = [];
        const falseArr = [];
        arr.forEach((x) => {
          if (x.show()) trueArr.push(x);
          else falseArr.push(x);
        });
        const fullFuncName = trueArr[0]?.title;
        const [funcName, argNum] = findFunctionAndArgument(currentPart, currentPart.length - 1);
        const [, totalArg] = findFunctionAndArgument(currentPart, currentPart.length - 1) || ["", 0];
        const filtered = currentPart.split(/[,(]/);
        const paramFilterText = filtered[filtered.length - 1] || "";
        state.paramFilterText = paramFilterText;
        if (smartSearchFormulas.includes(fullFuncName) && funcName) {
          state.funcName = fullFuncName;
          state.argNum = argNum;
          state.totalArg = totalArg;

          const parameters = currentPart.match(/([\w\s]+)\(([^)]+)\)?/);
          state.parameters = parameters && parameters[2].split(",");
        } else {
          state.funcName = null;
          state.argNum = null;
          state.totalArg = null;
          state.parameters = [];
        }

        return [...trueArr, ...falseArr];
      }

      return recursiveFilter(state.data, queryParts);
    },
    callSmartItem({ commit, state, rootState, dispatch, getters, rootGetters }, { item, i18n }) {
      const smartSearchHelper = new SmartSearchHelper(rootState, rootGetters, dispatch, item, i18n);
      smartSearchHelper.callFunc();
    },
    async searchAndCall({ commit, state, rootState, dispatch, getters, rootGetters }, { value, i18n }) {
      const items = await dispatch("search", value);
      if (items.length === 0) return;
      const item = items[0];

      dispatch("callSmartItem", { item, i18n });
    },
    setSmartSearchPrevScope({ commit, state, rootState }, scope) {
      state.smartSearchPrevScope = scope;
    },
    addParameterId({ commit, state, rootState, dispatch }, item) {
      state.parameterIds = [...state.parameterIds, item];
    },
    recalculateParameters({ commit, state, rootState }, text) {
      const params = getParameters(text);
      state.parameterIds = state.parameterIds.filter((param) => params.includes(param.title));
    },
    resetParameterIds({ commit, state, rootState }) {
      state.parameterIds = [];
    },
  },
  getters: {},
};
function getParameters(text) {
  const match = text.match(/\(([^)]*)\)?/); // Match content inside parentheses, even if the closing bracket is missing
  if (match) {
    // Split the parameters by commas, preserving strings with spaces
    return match[1].split(",").map((param) => param.trim());
  }
  return [];
}
